import { PayloadAction, createSlice } from '@reduxjs/toolkit'
import { getPreferences, SESSION_WORKLIST_PREFERENCES } from './preferenceStorage'
import { asFiniteNumber, isRecord } from '~/shared/lib/guards'

interface WorklistVisibilityPreference {
  isVisible: boolean
}

export interface FilterItem {
  field: string
  operator: string
  value: string
}

export interface WorklistFilterPreference {
  linkOperator: string
  items: FilterItem[]
}

interface WorklistPreferences {
  visibility?: { [field: string]: WorklistVisibilityPreference }
  filter?: WorklistFilterPreference
}

interface WorklistPreferencesState {
  byId: { [key: number]: WorklistPreferences }
  lastViewedWorklistId?: number
}
const INITIAL_STATE: WorklistPreferencesState = {
  byId: {},
}

/**
 * `filters` is stringified straight into the worklist request, so every field the request depends
 * on is checked. `value` is left unconstrained on purpose: the grid builds it from
 * `GridFilterItem['value']`, which is an array for multi-select operators and a number for numeric
 * ones, so requiring the `string` the type declares would reject filters the UI itself creates.
 */
const isFilterItem = (value: unknown): value is FilterItem =>
  isRecord(value) && typeof value.field === 'string' && typeof value.operator === 'string'

export const isFilterPreference = (value: unknown): value is WorklistFilterPreference =>
  isRecord(value) &&
  typeof value.linkOperator === 'string' &&
  Array.isArray(value.items) &&
  value.items.every(isFilterItem)

/**
 * Per-field rather than all-or-nothing: the keys are grid column names, so a column renamed since
 * the preference was stored is expected, and dropping that one entry keeps the operator's choices
 * for every column that still exists.
 */
const asVisibility = (value: unknown): WorklistPreferences['visibility'] => {
  if (!isRecord(value)) return undefined
  const visibility: NonNullable<WorklistPreferences['visibility']> = {}
  for (const [field, preference] of Object.entries(value)) {
    if (isRecord(preference) && typeof preference.isVisible === 'boolean') {
      visibility[field] = { isVisible: preference.isVisible }
    }
  }
  return visibility
}

/**
 * Restores the persisted preferences, ignoring anything that no longer fits the shape.
 *
 * This runs as the slice's `initialState`, so a value that does not fit is a broken store rather
 * than a broken render: `byId` is indexed unguarded by both reducers and `WorkUnitTable`. The
 * stored copy is the whole slice state written back on every change, and it lives in
 * `localStorage` despite the key's name — so a shape this app no longer writes, left by an
 * earlier version, would otherwise persist across every reload until the operator cleared the key.
 */
const getInitialState = (): WorklistPreferencesState => {
  const raw = getPreferences(SESSION_WORKLIST_PREFERENCES)
  if (!raw) return INITIAL_STATE

  let parsed: unknown
  try {
    parsed = JSON.parse(raw)
  } catch (_) {
    return INITIAL_STATE
  }
  if (!isRecord(parsed) || !isRecord(parsed.byId)) return INITIAL_STATE

  const byId: WorklistPreferencesState['byId'] = {}
  for (const [key, preference] of Object.entries(parsed.byId)) {
    const worklistId = asFiniteNumber(Number(key))
    if (worklistId === null || !isRecord(preference)) continue
    const visibility = asVisibility(preference.visibility)
    byId[worklistId] = {
      ...(visibility && { visibility }),
      ...(isFilterPreference(preference.filter) && { filter: preference.filter }),
    }
  }

  const lastViewedWorklistId = asFiniteNumber(parsed.lastViewedWorklistId)
  return { byId, ...(lastViewedWorklistId !== null && { lastViewedWorklistId }) }
}

export const worklistPreferencesSlice = createSlice({
  name: 'worklistPreference',
  initialState: getInitialState(),
  reducers: {
    setVisibility: (
      state,
      {
        payload: preference,
      }: PayloadAction<{ worklistId: number; field: string; isVisible: boolean }>,
    ) => {
      const worklistPreferences = (state.byId[preference.worklistId] ??= {})
      worklistPreferences.visibility = {
        ...worklistPreferences.visibility,
        [preference.field]: {
          isVisible: preference.isVisible,
        },
      }
    },
    setFilter: (
      state,
      {
        payload: worklistFilter,
      }: PayloadAction<{ worklistId: number; filter: WorklistFilterPreference }>,
    ) => {
      const worklistPreferences = (state.byId[worklistFilter.worklistId] ??= {})
      worklistPreferences.filter = worklistFilter.filter

      if (worklistFilter.filter.items.length === 0) {
        delete worklistPreferences.filter
      }
    },
    setLastViewedWorklistId: (state, { payload: worklistId }: PayloadAction<number>) => {
      state.lastViewedWorklistId = worklistId
    },
  },
})
